import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../../types/common/types';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { GzAccordionWebComponent } from '../../../../ui/gzAccordion';
import { GzInfoBoxWebComponent } from '../../../../ui/gzInfoBox';
import { QuarantineHoursDefault, QuarantineHoursOptions } from '../types/PackageMiscellaneousFormViewData';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { Feature } from '@nirvana/shared/constants';
import { PackageType } from '../../../packagesGrid/types';
import { translateFn } from '../../../../utils/translator.helper';

export class PackageMiscellaneousFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;

  public dom: {
    $wrapper: HTMLElement;

    $miscellaneousAccordion: GzAccordionWebComponent;
    $submitCrashDumpsCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $submitQuarantineSection: HTMLElement;
    $submitQuarantineCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $submitQuarantineHoursDropdown: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $submitSuspiciousCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $submitEndpointHealthFeedbackCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $endpointHealthFeedbackWarning: GzInfoBoxWebComponent;
    $useGlobalProtectiveNetworkCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $useGlobalProtectiveNetworkWarning: GzInfoBoxWebComponent;
  };

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('#package-miscellaneous-form-wrapper'),

      $miscellaneousAccordion: this.shadow.querySelector('#miscellaneous-section'),
      $submitCrashDumpsCheckbox: this.shadow.querySelector('#module-submit-crash-dumps'),
      $submitQuarantineSection: this.shadow.querySelector('#submit-quarantine-section'),
      $submitQuarantineCheckbox: this.shadow.querySelector('#module-submit-quarantine'),
      $submitQuarantineHoursDropdown: this.shadow.querySelector('#module-submit-quarantine-hours'),
      $submitSuspiciousCheckbox: this.shadow.querySelector('#module-submit-suspicious'),
      $submitEndpointHealthFeedbackCheckbox: this.shadow.querySelector('#module-submit-endpoint-health-feedback'),
      $endpointHealthFeedbackWarning: this.shadow.querySelector('#module-submit-endpoint-health-feedback-warning'),
      $useGlobalProtectiveNetworkCheckbox: this.shadow.querySelector('#module-use-global-protective-network'),
      $useGlobalProtectiveNetworkWarning: this.shadow.querySelector('#module-use-global-protective-network-warning'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations(): void {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$miscellaneousAccordion.accordionTitle = translateFn('MISCELLANEOUS');
    this.dom.$submitCrashDumpsCheckbox.Input.label = translateFn('Submit crash dumps');
    this.dom.$submitQuarantineCheckbox.Input.label = translateFn(
      'Submit quarantined files to Bitdefender Labs every (hours)'
    );
    this.dom.$submitSuspiciousCheckbox.Input.label = translateFn('Submit suspicious executables to Bitdefender');
    this.dom.$submitEndpointHealthFeedbackCheckbox.Input.label = translateFn('Send feedback regarding endpoint health');
    this.dom.$useGlobalProtectiveNetworkCheckbox.Input.label = translateFn(
      'Use Bitdefender Global Protective Network to enhance protection'
    );

    this.dom.$endpointHealthFeedbackWarning.innerHTML = this.dependencies.translateFn(
      'The feedback contains solely Bitdefender product information, for the purpose of improving the services. Disabling this option may prevent Bitdefender from correctly identifying and solving the issues you may encounter with the security agents.'
    );
    this.dom.$useGlobalProtectiveNetworkWarning.innerHTML = this.dependencies.translateFn(
      'Disabling this option will result in a major efficiency decrease of the Antimalware and Network Protection modules. You may experience the following: A large amount of false positive / negative detections Delays in detecting zero-day attacks Hybrid Scan engines efficiency significantly reduced Web Traffic filtering negatively affected Other modules relying on Antimalware being affected as well If using Hybrid Scan engines, you must switch to Local Scan engines.'
    );
    this.dom.$submitQuarantineHoursDropdown.Input.noResultsFound = this.commonStrings.noResultsFound;
  }

  public setLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  public removeLoadingState(): void {
    this.resetTemplateState(this.dom.$wrapper);
  }

  buildSuccessView(viewData) {
    this.createFormGroup();
    this.initializeQuarantineHoursDropdown();
    this.setDefaultValuesForFratCreateContext();
    this.formGroup.setDisabled(true);
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private createFormGroup(): void {
    this.formGroup = FormGroup.build({
      settings: {
        miscSettings: {
          submitCrashDumps: this.dom.$submitCrashDumpsCheckbox,
          submitQuarantine: this.dom.$submitQuarantineCheckbox,
          submitQuarantineHours: this.dom.$submitQuarantineHoursDropdown,
          submitSuspicious: this.dom.$submitSuspiciousCheckbox,
          submitEndpointHealthFeedback: this.dom.$submitEndpointHealthFeedbackCheckbox,
          useGlobalProtectiveNetwork: this.dom.$useGlobalProtectiveNetworkCheckbox,
        },
      },
    });
  }

  private initializeQuarantineHoursDropdown(): void {
    this.dom.$submitQuarantineHoursDropdown.Input.options = QuarantineHoursOptions;
    this.dom.$submitQuarantineHoursDropdown.Input.setFormValue(QuarantineHoursDefault.value);
    this.dom.$submitQuarantineHoursDropdown.Input.placeholder = QuarantineHoursDefault.title;
  }

  private setDefaultValuesForFratCreateContext(): void {
    if (this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_FRAT_VISIBLE)) {
      this.formGroup.value = {
        settings: {
          miscSettings: {
            submitCrashDumps: false,
            submitSuspicious: false,
            submitEndpointHealthFeedback: false,
            useGlobalProtectiveNetwork: false,
          },
        },
      };
      this.dom.$miscellaneousAccordion.disabled = true;
    }
  }

  public setDefaultValuesForDefaultContext(): void {
    this.formGroup.value = {
      settings: {
        miscSettings: {
          submitCrashDumps: true,
          submitQuarantine: true,
          submitQuarantineHours: QuarantineHoursOptions[0].value,
          submitSuspicious: true,
          submitEndpointHealthFeedback: true,
          useGlobalProtectiveNetwork: true,
        },
      },
    };
  }

  public toggleEndpointHealthFeedbackWarningVisibility(checked: boolean): void {
    toggleVisibility(this.dom.$endpointHealthFeedbackWarning, !checked);
  }

  public toggleUseGlobalProtectiveNetworkWarningVisibility(checked: boolean): void {
    toggleVisibility(this.dom.$useGlobalProtectiveNetworkWarning, !checked);
  }

  public disableQuarantineHoursDropdown(checked: boolean): void {
    this.dom.$submitQuarantineHoursDropdown.disabled = !checked;
  }

  public setPackageData(packageData): void {
    this.formGroup.setDisabled(false);
    this.formGroup.value = {
      settings: {
        miscSettings: {
          submitCrashDumps: packageData.settings.miscSettings?.submitCrashDumps ?? false,
          submitQuarantine: packageData.settings.miscSettings?.submitQuarantine ?? false,
          submitQuarantineHours:
            packageData.settings.miscSettings?.submitQuarantineHours ?? QuarantineHoursDefault.value,
          submitSuspicious: packageData.settings.miscSettings?.submitSuspicious ?? false,
          submitEndpointHealthFeedback: packageData.settings.miscSettings?.submitEndpointHealthFeedback ?? false,
          useGlobalProtectiveNetwork: packageData.settings.miscSettings?.useGlobalProtectiveNetwork ?? false,
        },
      },
    };

    if (packageData.type === PackageType.Frat || packageData.type === PackageType.RelayForFrat) {
      this.dom.$miscellaneousAccordion.disabled = true;
    }
  }
}
